import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../../../context/FirebaseContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useAdminAuthTranslation } from './translations';
import LanguageSwitcher from '../../../components/layout/navbar/LanguageSwitcher/LanguageSwitcher';
import './AdminLoginPage.css';
import './AdminRegisterPage.css';

const AdminRegisterPage = () => {
    // Basic account info
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [ownerName, setOwnerName] = useState('');


    // Store information
    const [storeName, setStoreName] = useState('');
    const [storeDescription, setStoreDescription] = useState('');
    const [storeAddress, setStoreAddress] = useState('');

    // Form state
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [step, setStep] = useState(1); // 1: Account, 2: Store
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [slideDirection, setSlideDirection] = useState(''); // 'left' or 'right'
    const [isGoogleRegistration, setIsGoogleRegistration] = useState(false); // Track if using Google auth

    // Get current language and translation
    const { language } = useLanguage();
    const { t } = useAdminAuthTranslation(language);

    const {
        currentUser,
        isAuthenticatedAdmin,
        register,
        registerWithGoogle
    } = useFirebase();

    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        const checkAuth = async () => {
            if (currentUser) {
                const isValid = await isAuthenticatedAdmin();
                if (isValid) {
                    navigate('/admin/dashboard', { replace: true });
                }
            }
        };

        checkAuth();
    }, [currentUser, isAuthenticatedAdmin, navigate]);

    const validateStep = (currentStep) => {
        setError('');

        if (currentStep === 1) {
            // Always require owner name
            if (!ownerName.trim()) {
                setError(t('ownerNameIsRequired'));
                return false;
            }

            // Only validate email/password if not using Google registration
            if (!isGoogleRegistration) {
                if (!email.trim() || !password || !confirmPassword) {
                    setError(t('pleaseEnterBothEmailAndPassword'));
                    return false;
                }

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email.trim())) {
                    setError(t('pleaseEnterValidEmail'));
                    return false;
                }

                if (password.length < 6) {
                    setError(t('minimumCharacters'));
                    return false;
                }

                if (password !== confirmPassword) {
                    setError(t('passwordsDoNotMatch'));
                    return false;
                }
            }
        }

        if (currentStep === 2) {
            if (!storeName.trim()) {
                setError(t('storeNameIsRequired'));
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setSlideDirection('slide-left');
            setTimeout(() => {
                setStep(step + 1);
                setSlideDirection('slide-in-right');
            }, 200);
        }
    };

    const handlePrevious = () => {
        setError('');
        setSlideDirection('slide-right');
        setTimeout(() => {
            setStep(step - 1);
            setSlideDirection('slide-in-left');
        }, 200);
    };

    // Google Registration Handler
    const handleGoogleRegister = async () => {
        if (loading) return;

        // Validate store information (step 2 equivalent)
        if (!storeName.trim()) {
            setError(t('storeNameIsRequired'));
            return;
        }

        if (!ownerName.trim()) {
            setError(t('ownerNameIsRequired'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const storeData = {
                storeName: storeName.trim(),
                storeDescription: storeDescription.trim(),
                storeAddress: storeAddress.trim(),
                ownerName: ownerName.trim()
            };

            const result = await registerWithGoogle(storeData);

            if (result.registrationComplete) {
                setSuccessMessage(result.message || "Your store has been created successfully!");
                setShowSuccessModal(true);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Phone verification functions
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!validateStep(3)) return;

        setLoading(true);

        try {
            if (isGoogleRegistration) {
                // Use Google registration
                const storeData = {
                    storeName: storeName.trim(),
                    storeDescription: storeDescription.trim(),
                    storeAddress: storeAddress.trim(),
                    ownerName: ownerName.trim()
                };

                const result = await registerWithGoogle(storeData);

                if (result.registrationComplete) {
                    setSuccessMessage(result.message || "Your store has been created successfully with Google!");
                    setShowSuccessModal(true);
                }
            } else {
                // Use email/password registration
                const registrationData = {
                    email: email.trim(),
                    password,
                    ownerName: ownerName.trim(),
                    storeName: storeName.trim(),
                    storeDescription: storeDescription.trim(),
                    storeAddress: storeAddress.trim()
                };

                const result = await register(registrationData);

                if (result.registrationComplete) {
                    setSuccessMessage(result.message || "Your store has been created successfully!");
                    setShowSuccessModal(true);
                } else {
                    // Fallback - shouldn't happen with new logic
                    navigate('/admin/dashboard', { replace: true });
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin = () => {
        setShowSuccessModal(false);
        navigate('/admin/login', { replace: true });
    };

    return (
        <div className="register-container">
            {/* Language Switcher */}
            <div className="auth-language-switcher">
                <LanguageSwitcher />
            </div>

            {/* Decorative background elements */}
            <div className="register-bg-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
            </div>

            <div className="register-card">
                <div className="register-header">

                    <h1>{t('createYourStore')}</h1>
                    <p className="register-subtitle">{t('setupStoreSteps')}</p>

                    {/* Enhanced Progress indicator */}
                    <div className="register-progress">
                        <div className="progress-track">
                            <div
                                className="progress-fill"
                                style={{ width: step === 1 ? '50%' : '100%' }}
                            ></div>
                        </div>
                        <div className="progress-steps">
                            <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                                <div className="step-indicator">
                                    {step > 1 ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                            <polyline points="20,6 9,17 4,12"></polyline>
                                        </svg>
                                    ) : '1'}
                                </div>
                                <span>{t('accountDetails')}</span>
                            </div>
                            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                                <div className="step-indicator">2</div>
                                <span>{t('storeInfo')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="register-error">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                <form className="register-form" onSubmit={handleSubmit}>
                    {/* Step 1: Account Information */}
                    {step === 1 && (
                        <div className={`form-step-container ${slideDirection}`}>


                            <div className="form-fields">
                                {/* Google Registration Option - Priority */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsGoogleRegistration(true);
                                        setStep(2);
                                    }}
                                    disabled={loading}
                                    className="google-register-button"
                                >
                                    <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    {t('continueWithGoogleRegister')}
                                </button>

                                {/* Auth Divider */}
                                <div className="auth-divider">
                                    <span>{t('or')}</span>
                                </div>

                                <div className="input-group">
                                    <label>{t('ownerName')} <span className="required">{t('required')}</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                        <input
                                            type="text"
                                            value={ownerName}
                                            onChange={(e) => setOwnerName(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder="Enter your full name"
                                            className={ownerName ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>{t('emailAddress')} <span className="required">{t('required')}</span></label>
                                    <div className="input-wrapper">

                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder={t('enterYourEmail')}
                                            autoComplete="email"
                                            className={email ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>{t('password')} <span className="required">{t('required')}</span></label>
                                        <div className="input-wrapper">

                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                placeholder={t('password')}
                                                autoComplete="new-password"
                                                className={password ? 'has-value' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                {showPassword ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                        <small className="input-hint">{t('minimumCharacters')}</small>
                                    </div>

                                    <div className="input-group">
                                        <label>{t('confirmPassword')} <span className="required">{t('required')}</span></label>
                                        <div className="input-wrapper">

                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                placeholder={t('confirmPasswordPlaceholder')}
                                                autoComplete="new-password"
                                                className={confirmPassword ? 'has-value' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                disabled={loading}
                                            >
                                                {showConfirmPassword ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                        <circle cx="12" cy="12" r="3"></circle>
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Store Information */}
                    {step === 2 && (
                        <div className={`form-step-container ${slideDirection}`}>
                            <div className="form-step-header">

                                <h3>{t('storeInfo')}</h3>
                                <p>{isGoogleRegistration ? t('continueWithGoogleRegister') : t('setupStoreSteps')}</p>
                            </div>

                            <div className="form-fields">
                                <div className="input-group">
                                    <label>{t('storeName')} <span className="required">{t('required')}</span></label>
                                    <div className="input-wrapper">
                                        <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                            <polyline points="9,22 9,12 15,12 15,22"></polyline>
                                        </svg>
                                        <input
                                            type="text"
                                            value={storeName}
                                            onChange={(e) => setStoreName(e.target.value)}
                                            disabled={loading}
                                            required
                                            placeholder={t('enterStoreName')}
                                            className={storeName ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>Store Description <span className="optional">(Optional)</span></label>
                                    <div className="input-wrapper textarea-wrapper">
                                        <svg className="input-icon textarea-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="17" y1="10" x2="3" y2="10"></line>
                                            <line x1="21" y1="6" x2="3" y2="6"></line>
                                            <line x1="21" y1="14" x2="3" y2="14"></line>
                                            <line x1="17" y1="18" x2="3" y2="18"></line>
                                        </svg>
                                        <textarea
                                            value={storeDescription}
                                            onChange={(e) => setStoreDescription(e.target.value)}
                                            disabled={loading}
                                            rows="3"
                                            placeholder={t('storeDescriptionPlaceholder')}
                                            className={storeDescription ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>{t('storeAddress')} <span className="optional">({t('storeDescription')})</span></label>
                                    <div className="input-wrapper textarea-wrapper">
                                        <svg className="input-icon textarea-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        <textarea
                                            value={storeAddress}
                                            onChange={(e) => setStoreAddress(e.target.value)}
                                            disabled={loading}
                                            rows="2"
                                            placeholder={t('enterStoreAddress')}
                                            className={storeAddress ? 'has-value' : ''}
                                        />
                                    </div>
                                </div>


                            </div>
                        </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="form-navigation">
                        {step > 1 && (
                            <button
                                type="button"
                                onClick={handlePrevious}
                                disabled={loading}
                                className="nav-button secondary"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12,19 5,12 12,5"></polyline>
                                </svg>
                                {t('previous')}
                            </button>
                        )}

                        {step < 2 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={loading}
                                className="nav-button primary"
                            >
                                {t('next')}
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                    <polyline points="12,5 19,12 12,19"></polyline>
                                </svg>
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={loading}
                                className={`nav-button primary ${loading ? 'loading' : ''}`}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        {isGoogleRegistration ? t('creatingStoreWithGoogle') : t('creatingYourStore')}
                                    </>
                                ) : (
                                    <>
                                        {isGoogleRegistration ? (
                                            <>
                                                <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                                {t('createStoreWithGoogle')}
                                            </>
                                        ) : (
                                            <>
                                                {t('createMyStore')}
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                                                </svg>
                                            </>
                                        )}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>

                <div className="register-footer">
                    <p>{t('alreadyHaveAccount')} <a href="/admin/login">{t('signIn')}</a></p>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="success-modal-overlay">
                    <div className="success-modal">
                        <div className="success-animation">
                            <div className="success-checkmark">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path className="checkmark-circle" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline className="checkmark-check" points="22,4 12,14.01 9,11.01"></polyline>
                                </svg>
                            </div>
                        </div>
                        <h2>{t('congratulations')}</h2>
                        <p className="success-title">{t('storeCreatedSuccessfully')}</p>
                        <div className="success-details">
                            <div className="success-store-info">
                                <div className="success-avatar">
                                    {storeName ? storeName.charAt(0).toUpperCase() : 'S'}
                                </div>
                                <div>
                                    <strong>{storeName}</strong>
                                    <span>{email}</span>
                                </div>
                            </div>
                        </div>
                        <p className="success-message">{successMessage}</p>
                        <div className="success-actions">
                            <button
                                className="success-button primary"
                                onClick={handleGoToLogin}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                                    <polyline points="10,17 15,12 10,7"></polyline>
                                    <line x1="15" y1="12" x2="3" y2="12"></line>
                                </svg>
                                {t('goToLogin')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRegisterPage;